import { Component, EventEmitter, OnDestroy, OnInit, Output } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { BaseProduct, ProductCategories, productCategories } from "@models/product.model";
import { StructureInstance, StructureStatus, StructureBaseModel, ModelQuantityJson } from "@models/structure.model";
import { ProductService } from "../../services/product/product.service";
import { noSpacesValidator } from "../../shared/shared/no-leading-space-validator.directive";
import { nonNegativeValidator } from "../../shared/non-negative-validator.directive";
import { appConfig } from "../../../environments/app.config";
import { AdminService } from "../../services/admin/admin.service";
import { StructureService } from "../../services/structure/structure.service";
import { catchError, of, Subscription, switchMap } from "rxjs";
import { S3UploadService } from "../../services/s3-upoad/s3-upload.service";
import { discountedPriceValidator } from "../../shared/shared/discounted-price-validator.directive";
import { MatDialog } from "@angular/material/dialog";
import { GenericDialogComponent } from "../../shared/generic-dialog/generic-dialog.component";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { S3UploadCategoryName, S3UploadFileType } from "@models/fileUpload.model";
import { SceneManagerService } from "../../services/scene-manager/scene-manager.service";
import { SnackBarConfig } from "../../constants/snackbar.constants";
import { TranslateService } from "@ngx-translate/core";

@Component({
    selector: "app-saved-structures-view",
    templateUrl: "./saved-structures-view.component.html",
    styleUrls: ["./saved-structures-view.component.scss"],
    standalone: false,
})
export class SavedStructuresViewComponent implements OnInit, OnDestroy {
    public captureImages: string[] = [];
    public structure!: StructureInstance;
    public structureForm: FormGroup;
    public loading: boolean = false;
    public categories: string[] = [];
    public gstRates = appConfig.gstRates;
    public structureStatus = StructureStatus;
    public isReadOnly = false;
    public isGlb: boolean = false;
    private structureId!: number;
    private isRemovingImage = false;
    private structureImageSub!: Subscription;
    public commonSubcategories = appConfig.commonSubcategories;

    constructor(
        private formBuilder: FormBuilder,
        private productService: ProductService,
        private router: Router,
        private adminService: AdminService,
        private snackBar: MatSnackBar,
        private s3UploadService: S3UploadService,
        private structureService: StructureService,
        private route: ActivatedRoute,
        private dialog: MatDialog,
        private sceneManagerService: SceneManagerService,
        private translate: TranslateService,
    ) {
        this.categories = this.getFilteredCategories();

        this.structureForm = this.formBuilder.group(
            {
                structureName: ["", [Validators.required, noSpacesValidator()]],
                structureDescription: ["", [Validators.required, noSpacesValidator()]],
                actualPrice: [null, [Validators.required, nonNegativeValidator()]],
                discountedPrice: [null, nonNegativeValidator()],
                model3DJson: [null],
                modelQuantityJson: [null, [Validators.required]],
                blocksQuantity: [null, [Validators.required]],
                category: [null, [Validators.required]],
                subCategory: [null, [Validators.required]],
                gstRate: [0],
            },
            { validators: discountedPriceValidator() },
        );
    }

    ngOnInit(): void {
        this.route.queryParams.subscribe((params) => {
            this.structureId = +params["id"];
            if (this.structureId) {
                this.loadStructureDetails(this.structureId);
            }
        });

        this.structureImageSub = this.structureService.structureImage$.subscribe((captureImageData) => {
            if (captureImageData.image && captureImageData.action === "add" && !this.isRemovingImage) {
                this.addImageInS3(captureImageData.image);
            }
        });
        window.addEventListener("beforeunload", this.handleBeforeUnload); //reload
    }

    private getFilteredCategories(): string[] {
        const excludedCategory = ProductCategories.BuyBlocks;
        return productCategories.filter((category) => category !== excludedCategory);
    }

    private loadStructureDetails(structureId: number): void {
        this.adminService.getStructureDetails(structureId).subscribe({
            next: (structure) => {
                if (structure.structureData) {
                    this.structure = structure.structureData;
                    if (this.structure.structureData) {
                        const extension = this.sceneManagerService.getFileExtension(this.structure.structureData);
                        switch (extension) {
                            case "json":
                                this.isGlb = false;
                                break;
                            case "glb":
                                this.isGlb = true;
                                break;
                            default:
                                this.isGlb = true;
                                break;
                        }
                    }
                    this.patchFormValue();
                }
                this.isReadOnly = structure.structureData?.status !== StructureStatus.Published;
            },
        });
    }

    public getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    private patchFormValue(): void {
        this.structureForm.get("structureName")?.setValue(this.structure.structureName);
        this.structureForm.get("structureDescription")?.setValue(this.structure.structureDescription);
        this.structureForm.get("blocksQuantity")?.setValue(this.structure.modelQuantityJson?.totalBlock);
        if (this.structure.modelQuantityJson) {
            this.structureService.fetchStructureData(this.structure.modelQuantityJson.modelInfoUrl).subscribe((res) => {
                this.structureForm.get("modelQuantityJson")?.setValue(JSON.stringify(res));
            });
        }
        if (this.structure.Product) {
            this.structureForm.patchValue({
                actualPrice: this.structure.Product.price,
                discountedPrice: this.structure.Product.discountedPrice,
                model3DJson: this.structure.Product.model3DJson
                    ? JSON.stringify(this.structure.Product.model3DJson)
                    : null,
                category: this.structure.Product.category!,
                subCategory: this.structure.Product.subCategory!,
                gstRate: this.structure.Product.gstRate,
            });

            this.captureImages = this.structure.Product.images?.imagesUrl ?? [];
        }
    }

    public async addImageInS3(file: File): Promise<void> {
        this.loading = true;
        this.structureService.loadImage(this.loading);
        await this.s3UploadService
            .multipartOperation([file], S3UploadCategoryName.StructureCaptured, S3UploadFileType.Images)
            .then((newImageUrls) => {
                this.loading = false;
                this.structureService.loadImage(this.loading);
                const captureImageSet = new Set(this.captureImages);
                captureImageSet.add(newImageUrls[0]);
                this.captureImages = Array.from(captureImageSet);
            })
            .catch((error) => {
                this.loading = false;
                this.openSnackBar(error.message, SnackBarConfig.Actions.CLOSE);
            });
    }

    public removeImageFromS3(index: number): void {
        this.isRemovingImage = true; // Set the flag
        const imageUrl = this.captureImages[index];
        this.loading = true;
        this.productService.deleteFile(imageUrl).subscribe({
            next: () => {
                this.loading = false;
                this.captureImages.splice(index, 1);
                this.captureImages = Array.from(new Set(this.captureImages));
                this.structureService.removeImage(index);
                this.isRemovingImage = false; // Reset the flag
            },
            error: (error) => {
                this.loading = false;
                this.openSnackBar(error.error, SnackBarConfig.Actions.CLOSE);
            },
        });
    }

    public async removeAllImagesFromS3(): Promise<void> {
        this.isRemovingImage = true; // Set the flag
        if (!this.captureImages.length) {
            return;
        }
        this.productService.deleteFiles(this.captureImages).subscribe((deleted) => {
            if (deleted) {
                this.captureImages = [];
                this.structureService.removeAllImages();
            }
            this.isRemovingImage = false; // Reset the flag
        });
    }

    public isCheckApprove(): boolean {
        const structureName = this.structureForm.get("structureName")?.value?.trim();
        const actualPrice = this.structureForm.get("actualPrice")?.value;
        const category = this.structureForm.get("category")?.value;
        const subCategory = this.structureForm.get("subCategory")?.value;
        const isStructureNameValid = structureName && structureName.length > 0;
        const isActualPriceValid = actualPrice !== null && actualPrice !== undefined && actualPrice > 0;
        const isCategorySelected = category && category.length > 0 && subCategory && subCategory.length > 0;
        const capturedGlbUrl = this.structureService.capturedStructureGlbUrl;
        return (
            isStructureNameValid &&
            isActualPriceValid &&
            isCategorySelected &&
            this.captureImages.length > 0 &&
            (this.isGlb || capturedGlbUrl)
        );
    }

    public onModel3DJsonBlur(event: Event): void {
        const value = (event.target as HTMLTextAreaElement).value;
        const formattedValue = this.formatJson(value);
        this.structureForm.get("model3DJson")?.setValue(formattedValue);
    }

    public approveStructure(): void {
        if (!this.isCheckApprove()) {
            return;
        }

        const initialStructureData: StructureBaseModel = {
            status: StructureStatus.Approved,
        };

        this.adminService
            .updateStructureData(+this.structure?.id!, initialStructureData)
            .pipe(
                switchMap(() => {
                    const productData: BaseProduct = {
                        name: this.structureForm.get("structureName")?.value?.trim(),
                        description: this.structureForm.get("structureDescription")?.value?.trim(),
                        price: this.structureForm.get("actualPrice")?.value,
                        discountedPrice: this.structureForm.get("discountedPrice")?.value,
                        createdBy: this.structure?.userDetails?.id.toString()!,
                        isActive: true,
                        category: this.structureForm.get("category")?.value,
                        subCategory: this.structureForm.get("subCategory")?.value,
                        gstRate: this.structureForm.get("gstRate")?.value,
                        glbUrl: this.isGlb
                            ? this.structure.structureData
                            : this.structureService.capturedStructureGlbUrl,
                        model3DJson: this.structureForm.get("model3DJson")?.value,
                        modelQuantityJson: this.structureId
                            ? this.structure.modelQuantityJson
                            : this.structureForm.get("modelQuantityJson")?.value,
                        images: { imagesUrl: this.captureImages },
                    };
                    return this.productService.addProduct(productData);
                }),
                switchMap((response) => {
                    const finalStructureData: StructureBaseModel = {
                        productId: response.product.id,
                    };
                    return this.adminService.updateStructureData(+this.structure.id, finalStructureData);
                }),
                catchError((error) => {
                    this.openSnackBar(error.error, SnackBarConfig.Actions.OKAY);
                    return of(null); // Return an observable to continue the stream
                }),
            )
            .subscribe({
                next: (response) => {
                    if (response) {
                        this.openSnackBar(
                            this.getTranslation("structure.successMessage.approvedSuccessfully"),
                            SnackBarConfig.Actions.OKAY,
                        );
                        this.router.navigate(["/admin/structureReview"]);
                    }
                },
                error: (error) => {
                    this.openSnackBar(error.error, SnackBarConfig.Actions.OKAY);
                },
            });
    }

    public rejectStructure(): void {
        this.openRejectStructuredialogbox();
    }

    public openRejectStructuredialogbox(): void {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.RejectStructure,
                title: this.getTranslation("genericDialogBox.rejectStructure.title"),
                inputs: [
                    {
                        controlName: "reason",
                        value: "",
                        placeholder: this.getTranslation("genericDialogBox.rejectStructure.placeHolder.reason"),
                        type: "text",
                    },
                ],
                firstBtn: this.getTranslation("genericDialogBox.button.rejectBtn"),
                secondBtn: this.getTranslation("genericDialogBox.button.cancelBtn"),
            },
            autoFocus: false,
            restoreFocus: false,
        });
        dialogRef.componentInstance.saveClicked.subscribe((data) => {
            if (data) {
                const structureData: StructureBaseModel = {
                    status: StructureStatus.Rejected,
                    rejectReason: data.reason,
                };

                this.adminService.updateStructureData(+this.structure?.id!, structureData).subscribe({
                    next: () => {
                        this.openSnackBar(
                            this.getTranslation("structure.successMessage.rejectSuccessfully"),
                            SnackBarConfig.Actions.OKAY,
                        );
                        this.router.navigate(["/admin/structureReview"]);
                    },
                    error: (error) => {
                        this.openSnackBar(error.error, SnackBarConfig.Actions.OKAY);
                    },
                });
            }
        });
    }

    private openSnackBar(message: string, action: string): void {
        this.snackBar.open(message, action, {
            ...SnackBarConfig.Defaults,
        });
    }

    public redirectBack(): void {
        if (this.captureImages.length && this.structure.status === StructureStatus.Published) {
            this.removeAllImagesFromS3().then(() => {
                this.router.navigateByUrl("admin/structureReview");
            });
        } else {
            this.router.navigateByUrl("admin/structureReview");
        }
    }

    private handleBeforeUnload = (event: BeforeUnloadEvent) => {
        if (!this.captureImages.length) {
            return;
        }
        event.preventDefault();
        event.returnValue = "";
    };

    private formatJson(jsonString: string): string {
        try {
            return JSON.stringify(JSON.parse(jsonString), null, 2);
        } catch (e) {
            return jsonString;
        }
    }

    ngOnDestroy(): void {
        this.structureImageSub.unsubscribe();
        window.removeEventListener("beforeunload", this.handleBeforeUnload);
    }

    public openDialogImage(image: string) {
        this.dialog.open(GenericDialogComponent, {
            data: { imageSlider: [{ image, thumbnail: image }], componentName: DialogInvokingComponents.PreviewImage },
        });
    }
}
